import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import Styles from './ImageViewer.module.scss';
import closeIcon from 'assets/svg/close-icon.svg';
import prevIcon from 'assets/svg/previous-icon.svg';
import nextIcon from 'assets/svg/next-icon.svg';
import { ViewerItem } from 'store/reducers/imageViewer.reducer';

interface ImageViewerProps {
  items: ViewerItem[];
  index: number;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.2;

const ImageViewer: FunctionComponent<ImageViewerProps> = ({ items, index, onClose }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Which item is on screen. Seeded from the opened index; kept local so paging
  // with the arrows doesn't churn redux. Re-seeds whenever a new open happens.
  const [currentIndex, setCurrentIndex] = useState(index);
  useEffect(() => { setCurrentIndex(index); }, [index]);

  const hasMultiple = items.length > 1;
  const current: ViewerItem | undefined = items[currentIndex];
  const imageUrl = current?.url || '';
  const imageName = current?.name;
  const mediaType = current?.type || 'image';

  // refs for smooth updates without re-rendering each event
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  const isPanningRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const rafId = useRef<number | null>(null);
  const needsUpdate = useRef(false);

  // local state only to trigger initial layout / className
  const [, setTick] = useState(0);

  const goPrev = useCallback(() => {
    if (items.length < 2) return;
    setCurrentIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);

  const goNext = useCallback(() => {
    if (items.length < 2) return;
    setCurrentIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, goPrev, goNext]);

  // reset zoom/pan whenever the shown item changes
  useEffect(() => {
    scaleRef.current = 1;
    txRef.current = 0;
    tyRef.current = 0;
    applyTransform();
    setTick(v => v + 1);
  }, [currentIndex, imageUrl]);

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const scheduleUpdate = () => {
    if (needsUpdate.current) return;
    needsUpdate.current = true;
    rafId.current = requestAnimationFrame(() => {
      needsUpdate.current = false;
      applyTransform();
    });
  };

  const applyTransform = () => {
    if (!imgRef.current) return;
    const s = scaleRef.current;
    const tx = txRef.current;
    const ty = tyRef.current;
    imgRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${s})`;
    updateCursor();
  };

  // Cursor reflects zoom/pan state. Set imperatively (like the transform) so wheel/
  // drag updates don't need a re-render: grabbing while dragging, grab when zoomed
  // in, default arrow at 1×.
  const updateCursor = () => {
    if (!wrapRef.current) return;
    wrapRef.current.style.cursor = isPanningRef.current
      ? 'grabbing'
      : scaleRef.current > 1
        ? 'grab'
        : '';
  };

  // imperative wheel listener with passive:false
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      const next = clamp(scaleRef.current + dir * ZOOM_STEP, MIN_SCALE, MAX_SCALE);
      scaleRef.current = next;
      if (next === 1) { txRef.current = 0; tyRef.current = 0; }
      scheduleUpdate();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as EventListener);
  }, [mediaType]);

  const onDoubleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    const next = scaleRef.current === 1 ? 2 : 1;
    scaleRef.current = next;
    if (next === 1) { txRef.current = 0; tyRef.current = 0; }
    scheduleUpdate();
  };

  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (scaleRef.current === 1) return;
    isPanningRef.current = true;
    startXRef.current = e.clientX - txRef.current;
    startYRef.current = e.clientY - tyRef.current;
    updateCursor();
  };
  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isPanningRef.current) return;
    txRef.current = e.clientX - startXRef.current;
    tyRef.current = e.clientY - startYRef.current;
    scheduleUpdate();
  };
  const endPan = () => { isPanningRef.current = false; updateCursor(); };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => () => { if (rafId.current) cancelAnimationFrame(rafId.current); }, []);

  return (
    <div className={Styles.overlay} onClick={onBackdropClick} role="dialog" aria-modal="true">
      <button className={Styles.closeBtn} onClick={onClose} aria-label="Close image viewer">
        <img src={closeIcon} alt="close" />
      </button>

      {hasMultiple && (
        <button className={`${Styles.navBtn} ${Styles.navPrev}`} onClick={goPrev} aria-label="Previous">
          <img src={prevIcon} alt="" />
        </button>
      )}
      {hasMultiple && (
        <button className={`${Styles.navBtn} ${Styles.navNext}`} onClick={goNext} aria-label="Next">
          <img src={nextIcon} alt="" />
        </button>
      )}

      <div className={Styles.viewer}>
        {mediaType === 'video' ? (
          <div className={Styles.imageWrap}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video key={imageUrl} src={imageUrl} className={Styles.image} controls autoPlay />
          </div>
        ) : (
          <div
            ref={wrapRef}
            className={`${Styles.imageWrap} ${scaleRef.current > 1 ? Styles.canPan : ''}`}
            onDoubleClick={onDoubleClick}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={endPan}
            onMouseLeave={endPan}
          >
            <img
              ref={imgRef}
              src={imageUrl}
              alt={imageName || 'image'}
              className={Styles.image}
              draggable={false}
            />
          </div>
        )}

        {imageName && <div className={Styles.caption}>{imageName}</div>}
      </div>
    </div>
  );
};

export default ImageViewer;
